import React, { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  Box,
  Typography,
  Switch,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Grid,
  Divider,
} from '@mui/material';
import InfoHint from '../common/InfoHint';
import {
  RestartAlt as ResetIcon,
  Splitscreen as SidebarIcon,
  TableRows as TopBarIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useFeatureFlagsStore } from '../../stores/featureFlagsStore';
import {
  FEATURE_FLAG_IDS,
  FEATURE_FLAG_LABELS,
  FEATURE_FLAG_PATHS,
  type FeatureFlagId,
  type FeatureFlagsState,
  type NavPlacement,
  type ClassificationBannerMode,
} from '../../types/featureFlags';

const cardSx = {
  p: { xs: 2, md: 2.5 },
  mb: 2,
  borderRadius: 2.5,
  border: 1,
  borderColor: 'divider',
  bgcolor: (t: { palette: { mode: string } }) =>
    t.palette.mode === 'dark' ? 'rgba(255,255,255,0.022)' : 'background.paper',
} as const;

const fieldLabelSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.4,
  mb: 1,
  minHeight: 24,
} as const;

const MODULE_IDS = FEATURE_FLAG_IDS.filter((id) => FEATURE_FLAG_PATHS[id]);
const EXTRA_IDS = FEATURE_FLAG_IDS.filter((id) => !FEATURE_FLAG_PATHS[id]);

/**
 * Admin-only: toggle which app tabs are visible / routable (DB-backed).
 * Settings is not gated — admins always reach this panel via Settings.
 */
const FeatureFlagsAdminPanel: React.FC = () => {
  const [selectedPreset, setSelectedPreset] = useState<string>('default');
  const flags = useFeatureFlagsStore(
    useShallow((s) =>
      FEATURE_FLAG_IDS.reduce((acc, id) => {
        acc[id] = s[id];
        return acc;
      }, {} as FeatureFlagsState)
    )
  );
  const setFlag = useFeatureFlagsStore((s) => s.setFlag);
  const setFlags = useFeatureFlagsStore((s) => s.setFlags);
  const resetToDefaults = useFeatureFlagsStore((s) => s.resetToDefaults);
  const navPlacement = useFeatureFlagsStore((s) => s.navPlacement);
  const setNavPlacement = useFeatureFlagsStore((s) => s.setNavPlacement);
  const participantLabelSingular = useFeatureFlagsStore((s) => s.participantLabelSingular);
  const participantLabelPlural = useFeatureFlagsStore((s) => s.participantLabelPlural);
  const setParticipantLabelSingular = useFeatureFlagsStore((s) => s.setParticipantLabelSingular);
  const setParticipantLabelPlural = useFeatureFlagsStore((s) => s.setParticipantLabelPlural);
  const courseLabelSingular = useFeatureFlagsStore((s) => s.courseLabelSingular);
  const courseLabelPlural = useFeatureFlagsStore((s) => s.courseLabelPlural);
  const setCourseLabelSingular = useFeatureFlagsStore((s) => s.setCourseLabelSingular);
  const setCourseLabelPlural = useFeatureFlagsStore((s) => s.setCourseLabelPlural);
  const classificationBannerMode = useFeatureFlagsStore((s) => s.classificationBannerMode);
  const setClassificationBannerMode = useFeatureFlagsStore((s) => s.setClassificationBannerMode);

  const apiBaseUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const apiDocsUrl = `${apiBaseUrl}/docs`;
  const modulesOn = MODULE_IDS.filter((id) => flags[id]).length;

  const applyPreset = (preset: string) => {
    setSelectedPreset(preset);

    if (preset === 'default') {
      setNavPlacement('left');
      setClassificationBannerMode('commercial');
      setParticipantLabelSingular('Entity');
      setParticipantLabelPlural('Entities');
      setCourseLabelSingular('Domain');
      setCourseLabelPlural('Domains');
      setFlags({
        dashboard: true,
        analysis: true,
        scheduledAnalyses: true,
        results: true,
        cop: true,
        pathfinder: true,
        linkAnalysis: true,
        patternOfLife: true,
        itinerary: true,
        dashboardBuilder: true,
        rfDashboard: false,
      });
      return;
    }

    if (preset === 'class') {
      setNavPlacement('left');
      setClassificationBannerMode('cui');
      setParticipantLabelSingular('Student');
      setParticipantLabelPlural('Students');
      setCourseLabelSingular('Course');
      setCourseLabelPlural('Courses');
      setFlags({
        dashboard: true,
        analysis: true,
        scheduledAnalyses: true,
        results: true,
        cop: true,
        pathfinder: true,
        linkAnalysis: true,
        patternOfLife: true,
        itinerary: true,
        dashboardBuilder: true,
        rfDashboard: true,
      });
      return;
    }

    if (preset === 'dfp') {
      setNavPlacement('left');
      setClassificationBannerMode('unclassified');
      setParticipantLabelSingular('Entity');
      setParticipantLabelPlural('Entities');
      setCourseLabelSingular('Mission');
      setCourseLabelPlural('Missions');
      setFlags({
        dashboard: true,
        analysis: true,
        scheduledAnalyses: true,
        results: true,
        cop: true,
        pathfinder: true,
        linkAnalysis: true,
        patternOfLife: true,
        itinerary: true,
        dashboardBuilder: true,
        rfDashboard: true,
      });
      return;
    }

    if (preset === 'tracer') {
      setNavPlacement('top');
      setClassificationBannerMode('commercial');
      setParticipantLabelSingular('Entity');
      setParticipantLabelPlural('Entities');
      setCourseLabelSingular('Domain');
      setCourseLabelPlural('Domains');
      setNavPlacement('top');
      setFlags({
        dashboard: false,
        analysis: true,
        scheduledAnalyses: true,
        results: true,
        cop: false,
        pathfinder: true,
        linkAnalysis: true,
        patternOfLife: false,
        itinerary: false,
        dashboardBuilder: false,
        rfDashboard: false,
      });
    }
  };

  const FlagRow: React.FC<{ id: FeatureFlagId; beta?: boolean }> = ({ id, beta }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ py: 1.15, minHeight: 52 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography sx={{ fontWeight: 650, fontSize: '0.9rem' }}>
            {FEATURE_FLAG_LABELS[id]}
          </Typography>
          {beta && (
            <Chip label="beta" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }} />
          )}
        </Stack>
      </Box>
      <Switch
        checked={flags[id]}
        onChange={(_, checked) => setFlag(id, checked)}
        color="primary"
      />
    </Stack>
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
        spacing={1}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.4 }}>
            Modules and flags
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tabs, wording, and feature toggles
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Chip
            icon={<CheckIcon sx={{ fontSize: 16 }} />}
            label="Saved"
            size="small"
            variant="outlined"
            sx={{ height: 26 }}
          />
          <Button size="small" href={apiBaseUrl} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>
            API
          </Button>
          <Button size="small" href={apiDocsUrl} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>
            Docs
          </Button>
        </Stack>
      </Stack>

      <Box sx={cardSx}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Box sx={fieldLabelSx}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>Preset</Typography>
              <InfoHint title="Presets">
                Applies a predefined module and terminology configuration.
              </InfoHint>
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel id="modules-preset-label">Preset</InputLabel>
              <Select<string>
                labelId="modules-preset-label"
                id="modules-preset-select"
                label="Preset"
                value={selectedPreset}
                onChange={(e) => applyPreset(e.target.value)}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="class">Class</MenuItem>
                <MenuItem value="dfp">DFP</MenuItem>
                <MenuItem value="tracer">Tracer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={fieldLabelSx}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>Classification banner</Typography>
              <InfoHint title="Classification banner">
                Controls the top and bottom fixed strips across the app. Commercial hides both;
                UNCLASSIFIED and CUI keep the same layout with different colors and wording.
              </InfoHint>
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel id="classification-banner-label">Banner mode</InputLabel>
              <Select<ClassificationBannerMode>
                labelId="classification-banner-label"
                id="classification-banner-select"
                label="Banner mode"
                value={classificationBannerMode}
                onChange={(e) =>
                  setClassificationBannerMode(e.target.value as ClassificationBannerMode)
                }
              >
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="unclassified">UNCLASSIFIED</MenuItem>
                <MenuItem value="cui">CUI</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={fieldLabelSx}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>Domain wording</Typography>
              <InfoHint title="Domain wording">
                Replaces visible “Domain” / “Domains” labels (e.g. filters, dropdowns). Does not
                change API fields like course_id or the internal “All Courses” select value.
              </InfoHint>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Singular"
                placeholder="Domain"
                value={courseLabelSingular}
                onChange={(e) => setCourseLabelSingular(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Plural"
                placeholder="Domains"
                value={courseLabelPlural}
                onChange={(e) => setCourseLabelPlural(e.target.value)}
                size="small"
                fullWidth
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={fieldLabelSx}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>Entity wording</Typography>
              <InfoHint title="Entity wording">
                Replaces visible “Entity” / “Entities” labels across the app. Does not change
                sign-in roles, API fields, or entity IDs (e.g. Student-01 in data).
              </InfoHint>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Singular"
                placeholder="Entity"
                value={participantLabelSingular}
                onChange={(e) => setParticipantLabelSingular(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Plural"
                placeholder="Entities"
                value={participantLabelPlural}
                onChange={(e) => setParticipantLabelPlural(e.target.value)}
                size="small"
                fullWidth
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Box sx={fieldLabelSx}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>Menu layout</Typography>
              <InfoHint title="Menu layout">
                Use a classic left sidebar or a horizontal top navigation bar. On small screens,
                the top layout uses a menu drawer.
              </InfoHint>
            </Box>
            <ToggleButtonGroup
              exclusive
              value={navPlacement}
              onChange={(_, value: NavPlacement | null) => {
                if (value) setNavPlacement(value);
              }}
              aria-label="Navigation placement"
              size="small"
            >
              <ToggleButton value="left" aria-label="Left sidebar" sx={{ textTransform: 'none', px: 1.75 }}>
                <SidebarIcon sx={{ mr: 1, fontSize: 18 }} />
                Left sidebar
              </ToggleButton>
              <ToggleButton value="top" aria-label="Top bar" sx={{ textTransform: 'none', px: 1.75 }}>
                <TopBarIcon sx={{ mr: 1, fontSize: 18 }} />
                Top bar
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Box>

      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 750 }}>
              Modules
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {modulesOn} of {MODULE_IDS.length}
            </Typography>
            <InfoHint title="Tab visibility">
              Disabled tabs are hidden from the menu. Direct URLs to a disabled tab redirect to
              Command Center.
            </InfoHint>
          </Stack>
          <Button
            size="small"
            startIcon={<ResetIcon sx={{ fontSize: 16 }} />}
            onClick={resetToDefaults}
            sx={{ textTransform: 'none' }}
          >
            Reset to defaults
          </Button>
        </Stack>
        <Grid container columnSpacing={4}>
          {MODULE_IDS.map((id, index) => (
            <Grid item xs={12} md={6} key={id}>
              {index >= 2 && <Divider />}
              {index < 2 && index > 0 && (
                <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
              )}
              <FlagRow id={id} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {EXTRA_IDS.length > 0 && (
        <Box sx={cardSx}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 750 }}>
              Feature flags
            </Typography>
            <InfoHint title="Feature flags">
              Extra capabilities that are not a top-level tab path.
            </InfoHint>
          </Stack>
          <Grid container columnSpacing={4}>
            {EXTRA_IDS.map((id, index) => (
              <Grid item xs={12} md={6} key={id}>
                {index >= 2 && <Divider />}
                <FlagRow id={id} beta />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default FeatureFlagsAdminPanel;
